import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { PropertyGroup, Property, Owner } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { FolderPlus, Trash2, Pencil, Check, X, Loader2, AlertTriangle } from "lucide-react";

const NONE_VALUE = "__none__";

export function usePropertyGroups() {
  return useQuery<PropertyGroup[]>({ queryKey: ["/api/app/property-groups"] });
}

interface PropertyGroupSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  allowNone?: boolean;
  testId?: string;
  disabled?: boolean;
  className?: string;
}

export function PropertyGroupSelect({
  value,
  onChange,
  placeholder = "Select a group",
  allowNone = false,
  testId = "select-property-group",
  disabled,
  className,
}: PropertyGroupSelectProps) {
  const groupsQuery = usePropertyGroups();
  const groups = groupsQuery.data ?? [];
  const hasLegacy = !!value && !groups.some((g) => g.name === value);

  return (
    <Select
      value={value === "" ? (allowNone ? NONE_VALUE : "") : value}
      onValueChange={(v) => onChange(v === NONE_VALUE ? "" : v)}
      disabled={disabled}
    >
      <SelectTrigger className={className} data-testid={testId} title={value || undefined}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allowNone && (
          <SelectItem value={NONE_VALUE} data-testid={`${testId}-option-none`}>— None —</SelectItem>
        )}
        {hasLegacy && (
          <SelectItem value={value} data-testid={`${testId}-option-legacy`}>
            {value} <span className="text-xs text-gray-400">(legacy)</span>
          </SelectItem>
        )}
        {groups.length === 0 && !hasLegacy ? (
          <div className="px-2 py-3 text-xs text-gray-500">
            No groups yet. Create one on the Properties page.
          </div>
        ) : (
          [...groups].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")).map((g) => (
            <SelectItem key={g.id} value={g.name} data-testid={`${testId}-option-${g.id}`}>
              {g.name}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

interface DeleteGroupDialogProps {
  group: PropertyGroup;
  otherGroups: PropertyGroup[];
  affectedProperties: Property[];
  onConfirm: (reassignToGroupId?: number) => void;
  onCancel: () => void;
  isPending: boolean;
}

function DeleteGroupDialog({
  group,
  otherGroups,
  affectedProperties,
  onConfirm,
  onCancel,
  isPending,
}: DeleteGroupDialogProps) {
  const [reassignToGroupId, setReassignToGroupId] = useState<string>("");

  const hasAffected = affectedProperties.length > 0;
  const hasOtherGroups = otherGroups.length > 0;

  const handleConfirm = () => {
    if (reassignToGroupId && reassignToGroupId !== NONE_VALUE) {
      onConfirm(parseInt(reassignToGroupId, 10));
    } else {
      onConfirm(undefined);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            Delete &ldquo;{group.name}&rdquo;?
          </DialogTitle>
          {hasAffected ? (
            <DialogDescription>
              The following {affectedProperties.length === 1 ? "property is" : "properties are"} assigned to this group and will be affected.
            </DialogDescription>
          ) : (
            <DialogDescription>
              This group has no properties assigned to it. Are you sure you want to delete it?
            </DialogDescription>
          )}
        </DialogHeader>

        {hasAffected && (
          <div className="mt-2 space-y-3">
            <ul className="max-h-36 overflow-y-auto rounded-md border divide-y text-sm">
              {affectedProperties.map((p) => (
                <li key={p.id} className="px-3 py-2 text-gray-700" data-testid={`text-affected-property-${p.id}`}>
                  {p.name}
                </li>
              ))}
            </ul>

            {hasOtherGroups ? (
              <div className="space-y-1">
                <p className="text-sm text-gray-600">Move these properties to another group, or leave them unassigned:</p>
                <Select
                  value={reassignToGroupId}
                  onValueChange={setReassignToGroupId}
                >
                  <SelectTrigger data-testid="select-reassign-group">
                    <SelectValue placeholder="— Leave unassigned —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE} data-testid="reassign-option-none">— Leave unassigned —</SelectItem>
                    {otherGroups.map((g) => (
                      <SelectItem key={g.id} value={String(g.id)} data-testid={`reassign-option-${g.id}`}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-sm text-amber-700 bg-amber-50 rounded-md px-3 py-2">
                No other groups exist. These properties will be left unassigned.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isPending} data-testid="button-cancel-delete-group">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending}
            data-testid="button-confirm-delete-group"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ManageGroupsDialogProps {
  triggerLabel?: string;
  triggerTestId?: string;
}

export function ManageGroupsDialog({
  triggerLabel = "Create Property Groups",
  triggerTestId = "button-manage-groups",
}: ManageGroupsDialogProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newOwnerId, setNewOwnerId] = useState<string>(NONE_VALUE);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [deletingGroup, setDeletingGroup] = useState<PropertyGroup | null>(null);

  const groupsQuery = usePropertyGroups();
  const groups = groupsQuery.data ?? [];

  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const properties = propertiesQuery.data ?? [];

  // Task #3740 — owners list for the per-group "Owner" dropdown. The endpoint is
  // gated (PM Pro + owner_portal beta), so a custom queryFn swallows non-OK
  // responses and returns an empty list — the owner UI then stays hidden for
  // hosts without access instead of throwing via the default fetcher.
  type OwnersListResponse = { owners: Owner[] };
  const ownersQuery = useQuery<OwnersListResponse>({
    queryKey: ["/api/app/operations/owners"],
    queryFn: async () => {
      const res = await fetch("/api/app/operations/owners");
      if (!res.ok) return { owners: [] };
      return res.json();
    },
    retry: false,
  });
  const owners = ownersQuery.data?.owners ?? [];
  const ownerNameById = new Map(owners.map((o) => [o.id, o.name]));

  const createMutation = useMutation({
    mutationFn: async ({ name, ownerId }: { name: string; ownerId: number | null }) => {
      const res = await apiRequest("POST", "/api/app/property-groups", { name });
      const created = await res.json();
      // Task #3740 — link the CRM owner via the dedicated gated endpoint rather
      // than the property-groups body (whose `ownerId` means the team owner).
      if (ownerId != null && created?.id != null) {
        await apiRequest("POST", `/api/app/operations/owners/${ownerId}/assign-group`, { groupId: created.id });
      }
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/property-groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owners"] });
      setNewName("");
      setNewOwnerId(NONE_VALUE);
    },
    onError: (err) => {
      toast({ title: parseErrorMessage(err, "Failed to create group"), variant: "destructive" });
    },
  });

  // Task #3740 — change a group's CRM owner from the manage-groups list.
  // assignGroupToOwner blocks reassigning a group already owned by someone else,
  // so when an owner is already set we unassign the old owner first, then assign.
  const ownerChangeMutation = useMutation({
    mutationFn: async ({ groupId, oldOwnerId, newOwnerId }: { groupId: number; oldOwnerId: number | null; newOwnerId: number | null }) => {
      if (oldOwnerId != null && oldOwnerId !== newOwnerId) {
        await apiRequest("POST", `/api/app/operations/owners/${oldOwnerId}/unassign-group`, { groupId });
      }
      if (newOwnerId != null && newOwnerId !== oldOwnerId) {
        await apiRequest("POST", `/api/app/operations/owners/${newOwnerId}/assign-group`, { groupId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/property-groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/owners"] });
    },
    onError: (err) => {
      toast({ title: parseErrorMessage(err, "Failed to update owner"), variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const res = await apiRequest("PATCH", `/api/app/property-groups/${id}`, { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/property-groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      setEditingId(null);
      setEditingName("");
    },
    onError: (err) => {
      toast({ title: parseErrorMessage(err, "Failed to rename group"), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id, reassignToGroupId }: { id: number; reassignToGroupId?: number }) => {
      await apiRequest("DELETE", `/api/app/property-groups/${id}`, reassignToGroupId != null ? { reassignToGroupId } : undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/property-groups"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      setDeletingGroup(null);
    },
    onError: (err) => {
      toast({ title: parseErrorMessage(err, "Failed to delete group"), variant: "destructive" });
      setDeletingGroup(null);
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    createMutation.mutate({
      name: trimmed,
      ownerId: newOwnerId === NONE_VALUE ? null : parseInt(newOwnerId, 10),
    });
  };

  const handleDeleteClick = (g: PropertyGroup) => {
    setDeletingGroup(g);
  };

  const handleDeleteConfirm = (reassignToGroupId?: number) => {
    if (!deletingGroup) return;
    deleteMutation.mutate({ id: deletingGroup.id, reassignToGroupId });
  };

  const handleDeleteCancel = () => {
    setDeletingGroup(null);
  };

  const affectedProperties = deletingGroup
    ? properties.filter((p) => p.propertyGroup === deletingGroup.name)
    : [];

  const otherGroups = deletingGroup
    ? groups.filter((g) => g.id !== deletingGroup.id)
    : [];

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" data-testid={triggerTestId} data-tour="properties-create-groups">
            <FolderPlus className="h-4 w-4 mr-1" />
            {triggerLabel}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Property Groups</DialogTitle>
            <DialogDescription>
              Create groups to organize properties. Use these groups in dropdowns across the app.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} className="mt-2 space-y-2">
            <div className="flex gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New group name (e.g. Example)"
                data-testid="input-new-group-name"
              />
              <Button
                type="submit"
                className="bg-brand hover:bg-brand-hover"
                disabled={createMutation.isPending || !newName.trim()}
                data-testid="button-create-group"
              >
                {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
              </Button>
            </div>
            {owners.length > 0 && (
              <Select value={newOwnerId} onValueChange={setNewOwnerId}>
                <SelectTrigger className="text-sm" data-testid="select-new-group-owner">
                  <SelectValue placeholder="— No owner —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE} data-testid="new-group-owner-option-none">— No owner —</SelectItem>
                  {owners.map((o) => (
                    <SelectItem key={o.id} value={String(o.id)} data-testid={`new-group-owner-option-${o.id}`}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </form>

          <div className="mt-4 max-h-[40vh] overflow-y-auto border rounded-lg divide-y">
            {groupsQuery.isLoading ? (
              <div className="p-4 text-sm text-gray-500 text-center">Loading…</div>
            ) : groups.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 text-center" data-testid="text-no-groups">
                No groups yet. Add your first one above.
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.id} className="flex items-center justify-between px-3 py-2" data-testid={`row-group-${g.id}`}>
                  {editingId === g.id ? (
                    <>
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="h-8 mr-2"
                        autoFocus
                        data-testid={`input-edit-group-${g.id}`}
                      />
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => {
                            const name = editingName.trim();
                            if (name && name !== g.name) updateMutation.mutate({ id: g.id, name });
                            else { setEditingId(null); setEditingName(""); }
                          }}
                          disabled={updateMutation.isPending}
                          data-testid={`button-save-group-${g.id}`}
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => { setEditingId(null); setEditingName(""); }}
                          data-testid={`button-cancel-edit-group-${g.id}`}
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-800 truncate" data-testid={`text-group-name-${g.id}`} title={g.name}>{g.name}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {owners.length > 0 && (
                          <Select
                            value={g.ownerId != null ? String(g.ownerId) : NONE_VALUE}
                            onValueChange={(v) => {
                              const next = v === NONE_VALUE ? null : parseInt(v, 10);
                              if (next === (g.ownerId ?? null)) return;
                              ownerChangeMutation.mutate({ groupId: g.id, oldOwnerId: g.ownerId ?? null, newOwnerId: next });
                            }}
                            disabled={ownerChangeMutation.isPending && ownerChangeMutation.variables?.groupId === g.id}
                          >
                            <SelectTrigger className="h-8 w-32 text-xs" data-testid={`select-group-owner-${g.id}`}>
                              <SelectValue placeholder="No owner">
                                {g.ownerId != null ? (ownerNameById.get(g.ownerId) ?? "No owner") : "No owner"}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE_VALUE} data-testid={`group-owner-option-none-${g.id}`}>— No owner —</SelectItem>
                              {owners.map((o) => (
                                <SelectItem key={o.id} value={String(o.id)} data-testid={`group-owner-option-${g.id}-${o.id}`}>
                                  {o.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => { setEditingId(g.id); setEditingName(g.name); }}
                          data-testid={`button-edit-group-${g.id}`}
                        >
                          <Pencil className="h-4 w-4 text-gray-500" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => handleDeleteClick(g)}
                          disabled={deleteMutation.isPending}
                          data-testid={`button-delete-group-${g.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="button-close-manage-groups">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {deletingGroup && (
        <DeleteGroupDialog
          group={deletingGroup}
          otherGroups={otherGroups}
          affectedProperties={affectedProperties}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isPending={deleteMutation.isPending}
        />
      )}
    </>
  );
}
